"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Icon from "@/components/ui/AppIcon";
import { blogArticles, CATEGORIES, categoryColors, BlogArticle } from "./data/articles";

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredArticles = useMemo(() => {
    return blogArticles.filter((article) => {
      const matchesCategory =
        activeCategory === "All" || article.category === activeCategory;
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !query ||
        article.title.toLowerCase().includes(query) ||
        article.excerpt.toLowerCase().includes(query) ||
        article.tags.some((tag) => tag.toLowerCase().includes(query));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-[#0C1B33]">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] to-[#0C1B33]" />
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #C9A84C 0%, transparent 50%), radial-gradient(circle at 80% 20%, #C9A84C 0%, transparent 40%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-px bg-gold/50" />
            <span className="text-[11px] font-display uppercase tracking-[0.2em] text-gold">
              Insights &amp; Resources
            </span>
            <div className="w-8 h-px bg-gold/50" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-700 text-cream mb-4 leading-tight">
            StanDave <span className="text-gold">Blog</span>
          </h1>
          <p className="text-cream/60 font-body text-[19px] max-w-2xl mx-auto leading-relaxed">
            Expert insights on asset management, logistics, global transactions, and business strategy from the StanDave team.
          </p>
        </div>
      </section>

      {/* Search + Filters */}
      <section className="sticky top-20 z-40 bg-[#0C1B33]/95 backdrop-blur-xl border-b border-[rgba(201,168,76,0.12)] py-4">
        <div className="max-w-7xl mx-auto px-6">
          {/* Search */}
          <div className="relative mb-4">
            <Icon
              name="MagnifyingGlassIcon"
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/30"
            />
            <input
              type="text"
              placeholder="Search articles by title or keyword…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(201,168,76,0.15)] rounded-sm pl-10 pr-4 py-2.5 text-sm font-body text-cream placeholder-cream/30 focus:outline-none focus:border-gold/40 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/30 hover:text-cream transition-colors"
              >
                <Icon name="XMarkIcon" size={14} className="" />
              </button>
            )}
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 text-[11px] font-display uppercase tracking-wider px-4 py-1.5 rounded-full border transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-gold text-[#0C1B33] border-gold font-700"
                    : "bg-transparent text-cream/50 border-[rgba(201,168,76,0.2)] hover:text-cream hover:border-gold/40"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid — light ash background */}
      <section className="bg-[#A8A8A8]">
        <div className="max-w-7xl mx-auto px-6 py-14">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-24">
              <Icon name="DocumentTextIcon" size={48} className="text-[#0C1B33]/20 mx-auto mb-4" />
              <p className="text-[#0C1B33]/40 font-body text-lg">No articles found.</p>
              <button
                onClick={() => { setSearchQuery(""); setActiveCategory("All"); }}
                className="mt-4 text-gold/70 hover:text-gold text-sm font-display uppercase tracking-wider transition-colors"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              <p className="text-[#0C1B33]/40 text-xs font-body mb-8">
                {filteredArticles.length} article{filteredArticles.length !== 1 ? "s" : ""}
                {activeCategory !== "All" ? ` in ${activeCategory}` : ""}
                {searchQuery ? ` matching "${searchQuery}"` : ""}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ArticleCard({ article }: { article: BlogArticle }) {
  return (
    <Link href={`/blog/${article.slug}`} className="group">
      <article className="relative flex flex-col h-full rounded-xl border border-[rgba(201,168,76,0.2)] bg-white hover:bg-[rgba(201,168,76,0.04)] hover:border-[rgba(201,168,76,0.4)] transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md">
        {/* Thumbnail placeholder */}
        <div className="relative h-44 bg-gradient-to-br from-[#0a1628] to-[#112244] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #C9A84C 0%, transparent 70%)" }} />
          <Icon name="DocumentTextIcon" size={40} className="text-gold/20" />
          <div className="absolute top-3 left-3">
            <span
              className={`text-[10px] font-display font-600 uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                categoryColors[article.category] ??
                "bg-[rgba(201,168,76,0.15)] text-gold border-[rgba(201,168,76,0.3)]"
              }`}
            >
              {article.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <h2 className="font-display font-600 text-[16px] leading-snug text-[#0C1B33] group-hover:text-gold transition-colors duration-200 mb-3 line-clamp-2">
            {article.title}
          </h2>
          <p className="text-[14px] font-body text-[#0C1B33]/55 leading-relaxed line-clamp-3 flex-1">
            {article.excerpt}
          </p>

          {/* Meta */}
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-[rgba(0,0,0,0.07)]">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center">
                <Icon name="UserIcon" size={12} className="text-gold/60" />
              </div>
              <div>
                <p className="text-[12px] font-display text-[#0C1B33]/60">{article.author}</p>
                <p className="text-[10px] font-body text-[#0C1B33]/35">{article.date}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-[10px] font-body text-[#0C1B33]/35">
              <Icon name="ClockIcon" size={11} className="" />
              {article.readTime}
            </div>
          </div>
        </div>

        {/* Top accent */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(201,168,76,0.5)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </article>
    </Link>
  );
}
